import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { type AssetSelectorState } from '../reducers/AssetSelectorReducer';
import { selectors as storySelectors } from 'store/selectors/StorySelectors';
import { ComponentType } from 'types';

import { getDatasetInVif as getDatasetInVifHelper } from 'lib/AssetSelectorHelper';

const selectAssetSelectorState = (state: { assetSelector: AssetSelectorState }): AssetSelectorState =>
  state.assetSelector;

export const selectBlockId = createSelector(selectAssetSelectorState, (state) => state.blockId);
export const selectComponentIndex = createSelector(selectAssetSelectorState, (state) => state.componentIndex);
export const selectComponentType = createSelector(selectAssetSelectorState, (state) => state.componentType);
export const selectComponentValue = createSelector(
  selectAssetSelectorState,
  (state) => state.componentProperties,
);
export const selectDataset = createSelector(selectAssetSelectorState, (state) => state.dataset);
export const selectStep = createSelector(selectAssetSelectorState, (state) => state.step);
export const selectIsEditingExisting = createSelector(
  selectAssetSelectorState,
  (state) => state.isEditingExisting === true,
);
export const selectErrorReason = createSelector(
  selectAssetSelectorState,
  (state) => state.componentProperties?.reason ?? null,
);
export const selectPreviewImageUrl = createSelector(
  selectAssetSelectorState,
  (state) => state.previewImageUrl ?? null,
);
export const selectIsGlobalFilter = createSelector(
  selectAssetSelectorState,
  (state) => state.componentType === ComponentType.GLOBAL_FILTER,
);

export const selectDatasetInVif = createSelector(selectAssetSelectorState, (state) => {
  if (!state.componentProperties || !state.componentProperties.vif) {
    return null;
  }
  return getDatasetInVifHelper(state as any);
});

// Returns the dataset chosen by the user from the dataset picker, if any
// NOTE: Inlined from getDatasetUserSelectedFromList helper - use this selector instead of the helper
export const selectDatasetUserSelectedFromList = createSelector(selectAssetSelectorState, (state) => {
  return state.componentProperties?.dataset?.datasetUid ?? null;
});

// Returns custom link configuration for measures
// NOTE: Inlined from getCustomLink helper - use this selector instead of the helper
export const selectCustomLink = createSelector(selectAssetSelectorState, (state) => {
  return state.componentProperties?.measure?.custom_link ?? { href: '', text: '' };
});

// Returns whether custom measure link is enabled
// NOTE: Inlined from isCustomMeasureLinkEnabled helper - use this selector instead of the helper
export const selectIsCustomMeasureLinkEnabled = createSelector(selectAssetSelectorState, (state) => {
  return state.componentProperties?.measure?.use_custom_link ?? false;
});

// Returns whether the current state is different from the original (when editing started)
// NOTE: Inlined from isDirty helper - use this selector instead of the helper
export const selectIsDirty = createSelector(selectAssetSelectorState, (state) => {
  const { componentType, componentProperties, originalComponentType, originalComponentProperties } = state;

  // layout sub-property is attached to component.value but not managed by asset selector
  const originalProperties = omit(originalComponentProperties, 'layout');

  const isTypeEqual = isEqual(componentType, originalComponentType);
  const isPropsEqual = isEqual(componentProperties, originalProperties);

  return !isTypeEqual || !isPropsEqual;
});

// Returns whether the measure is an in-situ measure (doesn't have a uid)
// NOTE: Inlined from isInsituMeasure helper - use this selector instead of the helper (safer with optional chaining)
export const isInsituMeasure = createSelector(selectAssetSelectorState, (state) => {
  return !state.componentProperties?.measure?.uid;
});

export const selectIsBusy = createSelector(selectAssetSelectorState, (state) => state.isBusy || false);

export const selectOriginalComponentType = createSelector(
  selectAssetSelectorState,
  (state) => state.originalComponentType,
);

export const selectOriginalComponentProperties = createSelector(
  selectAssetSelectorState,
  (state) => state.originalComponentProperties,
);

export const selectImageComponent = createSelector(selectAssetSelectorState, (state) => {
  const componentProperties = state.componentProperties || {};

  if (componentProperties === undefined) {
    return {};
  }

  if (state.componentType === 'author') {
    return get(componentProperties, 'image') || {};
  }

  return componentProperties;
});

export const selectOnlyAllowMeasureTiles = createSelector(
  [
    selectBlockId,
    (state: any) => state, // Pass full state to access story data
  ],
  (blockId, fullState) => {
    return !!(blockId && storySelectors.isBlockRestrictedToMeasureCardsOnly(fullState, blockId));
  },
);

export const selectCanProceed = createSelector(
  [selectIsBusy, selectStep, selectComponentType],
  (busy, step, componentType) => {
    return !busy && step && componentType;
  },
);

export const selectHasValidData = createSelector(
  [selectComponentValue, selectErrorReason],
  (componentValue, errorReason) => {
    return componentValue && !errorReason;
  },
);
